import styled from '@emotion/styled';
import { useRef, useState, useMemo, useEffect } from 'react';
import { PencilSimple } from '@phosphor-icons/react';
import { AccessControl } from 'features/accessControl';
import { Tooltip } from 'components/tooltip';
import { useEscapeToClose } from '../hooks';
import { useRowHeight } from '../../providers/TableProvider';
import { useEditableContextOptional } from './OptimizedTableProvider';
import { DotsMenuButton } from '../../components/DotsMenuButton';
import type { EditableTextCellProps } from '../types';
import { t } from '@lingui/macro';

/** Resolve the character offset closest to (x, y). Returns null when unsupported (cursor will land at end). */
function caretOffsetFromPoint(x: number, y: number): number | null {
  if (typeof document.caretPositionFromPoint === 'function') {
    return document.caretPositionFromPoint(x, y)?.offset ?? null;
  }
  return null;
}

export const EditableTextCell = <T extends { id: number | string }>({
  onUpdate,
  placeholder = t`Enter value...`,
  error,
  validate,
  maxChars,
  editable = true,
  disabled = false,
  hideActions = false,
  readModeEl,
  context: { getValue, row, column },
}: EditableTextCellProps<T>) => {
  const value = getValue<string>();
  const rowHeight = useRowHeight();
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState;
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const caretOffsetRef = useRef<number | null>(null);

  const enterEdit = (newLocalValue: string) => {
    setLocalValue(newLocalValue);
    setIsEditing(true);
    setEditableState?.({ rowIndex: row.index, columnId: column.id });
  };

  const exitEdit = () => {
    setIsEditing(false);
    setEditableState?.({ rowIndex: null, columnId: null });
  };

  // If this cell unmounts mid-edit (row navigation, page change), clear the
  // table-level editable state so the next mount doesn't inherit a stuck
  // "any cell editing" flag.
  useEffect(
    () => () => {
      if (isEditing) setEditableState?.({ rowIndex: null, columnId: null });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot cleanup
    []
  );

  const save = () => {
    if (localValue !== value) {
      if (maxChars != null && localValue.length > maxChars) {
        const over = localValue.length - maxChars;
        setLocalError(t`${over} characters too many. Try removing some text to continue.`);
        return;
      }
      const err = validate?.(localValue);
      if (err) {
        setLocalError(err);
        return;
      }
      onUpdate(localValue);
    }
    setLocalError(undefined);
    exitEdit();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
  };

  useEscapeToClose(isEditing, () => {
    setLocalError(undefined);
    exitEdit();
  });

  // Ref callback: fires as soon as the input mounts, focuses it and positions the caret.
  const inputCallbackRef = (input: HTMLInputElement | null) => {
    if (!input) return;
    input.focus();
    if (caretOffsetRef.current !== null) {
      const offset = Math.min(caretOffsetRef.current, localValue?.length ?? 0);
      input.setSelectionRange(offset, offset);
      caretOffsetRef.current = null;
    }
  };

  const displayValue = value || (editable ? '_' : '');
  const isInteractive = editable && !disabled;
  const showDotsMenu = isInteractive && !hideActions && row.original.id !== -1;
  const activeError = localError || error;

  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit',
        icon: <PencilSimple data-testid="pencil_simple_icon" size={16} />,
        onClick: () => enterEdit(value),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- enterEdit is stable across renders
    [value]
  );

  const handleCellClick = isInteractive
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        caretOffsetRef.current = caretOffsetFromPoint(e.clientX, e.clientY);
        enterEdit(value);
      }
    : undefined;

  const readFallback = readModeEl ? (
    <ReadModeElWrapper $editable={false} $rowHeight={rowHeight}>
      {readModeEl}
    </ReadModeElWrapper>
  ) : (
    <ReadWrapper
      $rowHeight={rowHeight}
      $editable={false}
      $disabled={disabled}
      title={activeError || undefined}
    >
      {displayValue}
    </ReadWrapper>
  );

  if (isEditing && !disabled) {
    return (
      <AccessControl write fallback={readFallback}>
        <Tooltip
          title={activeError ?? ''}
          open={!!activeError}
          placement="bottom-start"
          arrow
          error
          followCursor={false}
        >
          <StyledInput
            ref={inputCallbackRef}
            value={localValue}
            placeholder={placeholder}
            error={!!activeError}
            maxLength={maxChars}
            title={activeError || undefined}
            onChange={e => {
              const v = e.target.value;
              setLocalValue(v);
              if (maxChars != null && v.length > maxChars) {
                const over = v.length - maxChars;
                setLocalError(t`${over} characters too many. Try removing some text to continue.`);
              } else {
                setLocalError(undefined);
              }
            }}
            onBlur={save}
            onKeyDown={handleKeyDown}
          />
        </Tooltip>
      </AccessControl>
    );
  }

  const readContent = (
    <>
      {readModeEl ? (
        <ReadModeElWrapper
          $editable={isInteractive}
          $rowHeight={rowHeight}
          onClick={handleCellClick}
        >
          {readModeEl}
        </ReadModeElWrapper>
      ) : (
        <ReadWrapper
          $rowHeight={rowHeight}
          $editable={isInteractive}
          $disabled={disabled}
          title={activeError || undefined}
          onClick={handleCellClick}
        >
          {displayValue}
        </ReadWrapper>
      )}
      {showDotsMenu && (
        <DotsMenuButtonWrapper>
          <DotsMenuButton menuOptions={menuOptions} />
        </DotsMenuButtonWrapper>
      )}
    </>
  );

  return (
    <AccessControl write fallback={readFallback}>
      {readContent}
    </AccessControl>
  );
};

const DotsMenuButtonWrapper = styled.div`
  max-width: 18px;
`;

const ReadModeElWrapper = styled.div<{ $editable: boolean; $rowHeight: number }>`
  display: block;
  width: 100%;
  min-width: 0;
  line-height: ${p => p.$rowHeight}px;
  padding: 0 ${p => p.theme.s.sm};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  &:hover {
    background-color: ${p => p.$editable && p.theme.palette.utility.grey[100]};
  }
`;

const ReadWrapper = styled.div<{ $rowHeight: number; $editable: boolean; $disabled: boolean }>`
  display: block;
  width: 100%;
  line-height: ${p => p.$rowHeight}px;
  padding: 0 ${p => p.theme.s.sm};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
  ${p => (p.$disabled ? `background-color: ${p.theme.palette.bg.disabledSubtle};` : '')};
  ${p => (p.$disabled ? `color: ${p.theme.palette.text.disabledSubtle};` : '')};
  &:hover {
    background-color: ${p => p.$editable && p.theme.palette.utility.grey[100]};
  }
`;

const StyledInput = styled.input<{ error?: boolean }>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 0 ${p => p.theme.s.sm};
  background: transparent;
  border: none;
  // a little hacky here because first row is positioned differently than subsequent rows, so moved the red line up one pixel until we fix that
  box-shadow: ${p =>
    p.error ? `inset 0 -1px 0 white, inset 0 -2px 0 ${p.theme.palette.border.errorSolid}` : 'none'};
  outline: none;
  font-family: inherit;
  font-size: inherit;
  color: ${p => (p.error ? p.theme.palette.fg.errorPrimary : 'inherit')};
`;
